import React, { useContext, useEffect, useState } from 'react'
import { Divider, ExpandableListItem, List, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { TreeLink } from '../../app/Links'
import ObjectView from '../../common/ObjectView'
import { UserContext } from '../UserContext'
import useNotifications from '../../../hooks/useNotifications'
import { getPersonCount, getTreeGroups } from '../../../services/treeService'
import { usePageTitle } from '../../app/PageTitleContext'

async function enrichTree(trees, onError, t) {
  return Promise.all(
    trees.map(async (item) => {
      const { treeId } = item.tree

      const [personCount, groupsResponse] = await Promise.all([
        getPersonCount(treeId).catch((err) =>
          onError(
            t('tree.error.personCount', 'Error getting persons count for tree {treeId}: {message}', {
              treeId,
              message: err.message,
            })
          )
        ),
        item.treeType === 'SHARED_FAMILY_GROUP' ? getTreeGroups(treeId).catch(() => null) : Promise.resolve(null),
      ])

      const groupNames =
        groupsResponse?.data?.groups && Object.values(groupsResponse.data.groups).length > 0
          ? Object.values(groupsResponse.data.groups)
              .map((g) => g.name)
              .join(', ')
          : null

      return {
        ...item,
        displayName: groupNames ?? item.tree.name,
        tree: { ...item.tree, personCount: personCount?.data },
      }
    })
  )
}

export default function UserOwnedTrees() {
  usePageView({ page_detail: 'DMConsole:UserTrees' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.trees'))
  const { ownedTrees: initialOwnedTrees, participantTrees: initialParticipantTrees } = useContext(UserContext)
  const [familyGroupTrees, setFamilyGroupTrees] = useState([])
  const [researcherOwnedTrees, setResearcherOwnedTrees] = useState([])
  const [researcherSharedTrees, setResearcherSharedTrees] = useState([])
  const { error } = useNotifications()

  useEffect(() => {
    const sortByName = (a, b) => {
      if (a.tree.tombstoned !== b.tree.tombstoned) return a.tree.tombstoned ? 1 : -1
      return a.displayName.toUpperCase().localeCompare(b.displayName.toUpperCase())
    }

    const allOwned = initialOwnedTrees ?? []
    const allParticipant = initialParticipantTrees ?? []

    const ownedToEnrich = allOwned.length ? enrichTree(allOwned, error, t) : Promise.resolve([])
    const participantToEnrich = allParticipant.length ? enrichTree(allParticipant, error, t) : Promise.resolve([])

    Promise.all([ownedToEnrich, participantToEnrich]).then(([enrichedOwned, enrichedParticipant]) => {
      const ownedIds = new Set(enrichedOwned.map((item) => item.tree.treeId))
      const uniqueParticipant = enrichedParticipant.filter((item) => !ownedIds.has(item.tree.treeId))

      const isFamilyGroup = (item) => item.treeType === 'SHARED_FAMILY_GROUP'
      const isResearcher = (item) => item.treeType === 'RESEARCHER'
      setFamilyGroupTrees(
        [...enrichedOwned.filter(isFamilyGroup), ...uniqueParticipant.filter(isFamilyGroup)].sort(sortByName)
      )
      setResearcherOwnedTrees(enrichedOwned.filter(isResearcher).sort(sortByName))
      setResearcherSharedTrees(uniqueParticipant.filter(isResearcher).sort(sortByName))
    })
  }, [initialOwnedTrees, initialParticipantTrees, error, t])

  const renderTreeList = (trees) =>
    trees.map((tree) => (
      <React.Fragment key={tree.tree.treeId}>
        <span>
          <ExpandableListItem
            id={`listitem - ${tree.tree.treeId}`}
            primaryText={<TreeLink treeId={tree.tree.treeId} treeName={tree.displayName} treeType={tree.treeType} />}
            secondaryText={tree.tree.tombstoned ? t('tombstoned', 'Tombstoned') : null}
            selected={tree.tree.tombstoned}
          >
            <ObjectView object={tree.tree} fieldsToOmit={['devkeys']} />
          </ExpandableListItem>
        </span>
        <Divider />
      </React.Fragment>
    ))

  return (
    <>
      {familyGroupTrees.length > 0 && (
        <>
          <span>{t('user.trees.familyGroupTrees', 'Groups With Trees')}</span>
          <Divider />
          <List id="userFamilyGroupTreesList">{renderTreeList(familyGroupTrees)}</List>
        </>
      )}
      {researcherOwnedTrees.length > 0 && (
        <>
          <Separator size="lg" />
          <span>{t('user.trees.researcherOwnedTrees', 'Trees Created By User')}</span>
          <Divider />
          <List id="userResearcherOwnedTreesList">{renderTreeList(researcherOwnedTrees)}</List>
        </>
      )}
      {researcherSharedTrees.length > 0 && (
        <>
          <Separator size="lg" />
          <span>{t('user.trees.researcherSharedTrees', 'Trees Shared With User')}</span>
          <Divider />
          <List id="userResearcherSharedTreesList">{renderTreeList(researcherSharedTrees)}</List>
        </>
      )}
    </>
  )
}
